import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft, BadgeCheck, CalendarDays, MapPin, User, CheckCircle2, ScanLine } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function TicketVerify() {
  const { ticketId } = useParams();
  const { user } = useAuth();
  const [reg, setReg] = useState(null);
  const [status, setStatus] = useState("loading");
  const [checkin, setCheckin] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/tickets/verify/${ticketId}`).then((r) => { setReg(r.data); setStatus("ok"); }).catch(() => setStatus("notfound"));
  }, [ticketId]);

  const doCheckin = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tickets/${ticketId}/checkin`);
      setCheckin(data);
      if (data.status === "ok") toast.success("Checked in!");
      else toast("Already checked in earlier");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const alreadyIn = reg?.checked_in || checkin?.status === "already" || checkin?.status === "ok";

  return (
    <main className="pt-24 pb-20 min-h-screen flex items-center">
      <div className="max-w-lg mx-auto px-5 w-full">
        {status === "loading" && <div className="pt-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#E6197E]" /></div>}
        {status === "notfound" && (
          <div className="pt-10 text-center">
            <h1 className="font-heading text-2xl font-bold">Ticket not found</h1>
            <p className="text-zinc-400 mt-2">The ticket <span className="font-mono-accent">{ticketId}</span> is not valid.</p>
            <Link to="/events"><Button variant="outline" className="mt-6 border-white/15 bg-transparent rounded-full"><ArrowLeft className="w-4 h-4 mr-2" /> View Events</Button></Link>
          </div>
        )}
        {status === "ok" && (
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl border border-white/10 bg-[#131519] p-8 text-center" data-testid="ticket-verify-card">
            <div className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-400 mb-3"><BadgeCheck className="w-4 h-4" /> VALID TICKET</div>
            <h1 className="font-heading text-2xl font-extrabold">{reg.event_title}</h1>
            <div className="mt-5 space-y-2.5 text-left max-w-xs mx-auto">
              <div className="flex items-center gap-2.5 text-sm"><User className="w-4 h-4 text-[#E6197E]" /> {reg.name}</div>
              {reg.date && <div className="flex items-center gap-2.5 text-sm"><CalendarDays className="w-4 h-4 text-[#E6197E]" /> {reg.date}</div>}
              {reg.location && <div className="flex items-center gap-2.5 text-sm"><MapPin className="w-4 h-4 text-[#E6197E]" /> {reg.location}</div>}
            </div>
            <div className="mt-6 pt-5 border-t border-white/10">
              <div className="text-[11px] uppercase tracking-widest text-zinc-500">Ticket ID</div>
              <div className="font-mono-accent text-lg">{reg.ticket_id}</div>
            </div>

            {alreadyIn && (
              <div className="mt-5 rounded-xl bg-amber-500/10 border border-amber-500/40 p-3 text-sm text-amber-300 flex items-center justify-center gap-2" data-testid="ticket-checkedin-badge">
                <CheckCircle2 className="w-4 h-4" /> {checkin?.status === "ok" ? "Checked in just now" : `Already checked in${(reg?.checked_in_at || checkin?.checked_in_at) ? " · " + new Date(reg?.checked_in_at || checkin?.checked_in_at).toLocaleString() : ""}`}
              </div>
            )}

            {user && !alreadyIn && (
              <Button onClick={doCheckin} disabled={busy} className="mt-5 w-full bg-[#E6197E] hover:bg-[#C4136B] rounded-full font-semibold" data-testid="ticket-checkin-button">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ScanLine className="w-4 h-4 mr-2" /> Mark as Checked In</>}
              </Button>
            )}
            {!user && <p className="mt-4 text-xs text-zinc-500">Gate staff: sign in to check this ticket in.</p>}
          </motion.div>
        )}
      </div>
    </main>
  );
}
